import '../sp-spinner-overlay/sp-spinner-overlay';

import {customElement, property, state} from "lit/decorators.js";
import {css, html, LitElement, PropertyValues} from "lit";
import {styleMap} from "lit-html/directives/style-map.js";
import {when} from "lit/directives/when.js";

@customElement('sp-image')
export class SpImage extends LitElement {
    static styles = css`
        :host {
            position: relative;
            display: inline-block;
            height: 100%;
            width: 100%;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;

        }

        .badge {
            position: absolute;
            right: 0;
            bottom: 0;
            width: 18px;
            height: 18px;
            background-position: center center;
            background-size: 18px auto;
            background-repeat: no-repeat;
            border-radius: 9px;
        }

        sp-spinner-overlay {
            border-radius: inherit;
        }
    `;

    @property({type: String})
    image: Blob | string;
    // Show a spinner while loading
    @property({type: Boolean})
    showSpinner = true;
    // Displayed while no image present or loading
    @property({type: String})
    placeholderImage: string;
    // Displayed when error on image loading
    @property({type: String})
    errorImage: string;
    // Add a badge to the image
    @property({type: Object})
    badge: SpImageBadge

    @state()
    imageUrl;
    @state()
    loading = false;
    error = false;

    protected willUpdate(_changedProperties: PropertyValues) {
        super.willUpdate(_changedProperties);
        if (_changedProperties.has('image') && this.image) {
            this.error = false;
            if (this.imageUrl) URL.revokeObjectURL(this.imageUrl);
            this.imageUrl = typeof this.image === 'object' ? URL.createObjectURL(this.image) : this.sanitizeUrl(this.image);
            this.loading = true;
            const img = new Image();
            img.onload = () => {
                this.loading = false;
            }
            img.onerror = () => {
                this.error = true;
                this.loading = false;
            }
            img.src = this.imageUrl;
        }
    }

    protected render() {
        const showSpinner = this.loading && this.showSpinner;
        this.style.backgroundImage = this.getImage();
        return html`
            ${ when(showSpinner, () => html`<sp-spinner-overlay id="spinner-overlay" exportparts="spinner: spinner"></sp-spinner-overlay>`) }
            <slot></slot>
            ${ when(this.badge, () => html`
                <div class="badge" 
                     style=${styleMap({
                         backgroundImage: `url(${this.badge.icon})`,
                         ...this.badge?.backgroundColor && { backgroundColor: this.badge.backgroundColor },
                         ...(this.badge?.variant && this.badge.variant === 'circled') && {
                             backgroundSize: '10px auto'
                         }
                     })}
                     part="badge"></div>
            `) }
        `;
    }

    getImage() {
        if (this.loading || !this.image)
            return this.placeholderImage ?
                `url(${this.placeholderImage})` : 'unset';
        if (this.error)
            return this.errorImage ?
                `url(${this.errorImage})` : 'unset';
        return `url(${this.imageUrl})`;

    }

    sanitizeUrl(url: string) {
        // When the url contains round brackets "(" or ")", they cannot be assigned using styles.setProperty(), which is used by the styleMap
        url = url.replace(/\(/g, "%28");
        url = url.replace(/\)/g, "%29");
        return url;
    }
}

export interface SpImageBadge {
    icon: string;
    backgroundColor?: string;
    variant?: 'circled'
}